import { useEffect, useRef, useState, type ReactNode, type MouseEvent } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";

/** Paint acknowledgement before leaving the SPA; reset when restored from browser history. */
export function useLoginNavigation() {
  const [pending, setPending] = useState(false);
  const started = useRef(false);
  useEffect(() => {
    const reset = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      started.current = false;
      setPending(false);
    };
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);
  const start = (href: string) => {
    if (started.current) return;
    started.current = true;
    flushSync(() => setPending(true));
    window.location.href = href;
  };
  return { pending, start };
}

export function LoginLink({ href, className, children }: { href: string; className?: string; children: ReactNode }) {
  const { t } = useTranslation();
  const { pending, start } = useLoginNavigation();
  const click = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    start(href);
  };
  return <a href={href} className={className} onClick={click} aria-busy={pending} aria-disabled={pending || undefined}>
    {pending ? <span role="status">{t("login.pending")}</span> : children}
  </a>;
}
